// Live smart-room presence for one location — drives the person image on a
// room (ui/shared/RoomPresenceBadge), on both shells. See core/devices/roomPresence.
//
// Two triggers, one source (the server's verdict):
//   • every 30 s while the screen is visible — the 5-minute "unoccupied" turn
//     happens with no sensor event at all, so something has to ask;
//   • 1.5 s after a DOOR or MOTION event in this location (useRfSensors hears
//     the receivers live) — the server has stamped it by then, so the person
//     appears as someone walks in, not up to half a minute later.
//
// The MQTT topics are reference-counted (mqttClient), so listening here as
// well as on the same screen's sensor tiles costs nothing extra on the broker.
//
// ⚠️ An absent endpoint (the server not yet updated) is not an error to show:
// nothing is occupied, and it asks again only every 5 minutes.

import { useCallback, useEffect, useRef, useState } from 'react';
import { getSmartRoomState } from '../api/api';
import { presenceByRoom, isOccupied as occupiedNow } from '../devices/roomPresence';
import useRfSensors from './useRfSensors';

const POLL_MS = 30000;
const BACKOFF_MS = 5 * 60000;
const AFTER_EVENT_MS = 1500;

/**
 * @param locationId  every smart room of this location (rooms list, desktop sidebar)
 * @param opts.roomId instead: just this room — for a screen that may not know its
 *                    location (the phone's room screen opened by a direct link)
 * -> { presenceOf(roomId) -> presence | null, isOccupied(roomId) -> bool, reload }
 */
export default function useRoomPresence(locationId, { viewerUserId, roomId = null } = {}) {
  const [map, setMap] = useState(() => new Map());
  const failedRef = useRef(false);
  const eventTimer = useRef(null);

  const reload = useCallback(async () => {
    if (locationId == null && roomId == null) { setMap(new Map()); return; }
    try {
      const res = await getSmartRoomState(roomId || undefined);
      const rows = res && Array.isArray(res.data) ? res.data : null;
      failedRef.current = !rows;
      setMap(rows ? presenceByRoom(rows, roomId ? null : locationId) : new Map());
    } catch (e) {
      failedRef.current = true;
      setMap(new Map());
    }
  }, [locationId, roomId]);

  useEffect(() => {
    reload();
    let timer = null;
    let stopped = false;   // a reload in flight at unmount must not schedule another
    const schedule = () => {
      timer = setTimeout(async () => {
        if (typeof document === 'undefined' || !document.hidden) await reload();
        if (!stopped) schedule();
      }, failedRef.current ? BACKOFF_MS : POLL_MS);
    };
    schedule();
    const onVisible = () => { if (!document.hidden) reload(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [reload]);

  // A door or motion event here -> ask again shortly (repeats already collapsed).
  const onEvent = useCallback((sensor) => {
    const t = String(sensor?.RfType || '').toLowerCase();
    if (t !== 'door' && t !== 'motion') return;
    clearTimeout(eventTimer.current);
    eventTimer.current = setTimeout(reload, AFTER_EVENT_MS);
  }, [reload]);
  useEffect(() => () => clearTimeout(eventTimer.current), []);
  useRfSensors(roomId, { locationId: roomId ? null : (locationId ?? null), viewerUserId, onEvent });

  const presenceOf = useCallback((id) => map.get(String(id)) || null, [map]);
  const isOccupied = useCallback((id) => occupiedNow(map.get(String(id))), [map]);
  return { presenceOf, isOccupied, reload };
}
